import * as Yup from 'yup';
import { validateAttributeValue } from './validateAttributeValue';
import type { AttributeTypeKey } from 'BlockList/types';

/**
 * Yup schemas for each Attribute Type. Source of validation truth lives in
 * `validateAttributeValue.ts` — these schemas delegate so behavior stays in
 * sync. Used by the AddAttributeModal Formik form (validates on blur and
 * change by default; renders inline errors via Formik's touched/errors).
 */

type SingleValueType = Exclude<
  AttributeTypeKey,
  'drivers_license' | 'name_dob'
>;

const singleValueSchema = (type: SingleValueType): Yup.StringSchema =>
  Yup.string().test(
    'attribute-value',
    'Invalid value.',
    function (value: string | undefined) {
      const err = validateAttributeValue(type, value ?? '');
      return err ? this.createError({ message: err }) : true;
    }
  );

export const EMAIL_VALUE_SCHEMA = singleValueSchema('email');
export const EMAIL_DOMAIN_VALUE_SCHEMA = singleValueSchema('email_domain');
export const PHONE_NUMBER_VALUE_SCHEMA = singleValueSchema('phone_number');
export const VIN_VALUE_SCHEMA = singleValueSchema('vin');

export const ATTRIBUTE_VALUE_SCHEMAS: Record<
  SingleValueType,
  Yup.StringSchema
> = {
  email: EMAIL_VALUE_SCHEMA,
  email_domain: EMAIL_DOMAIN_VALUE_SCHEMA,
  phone_number: PHONE_NUMBER_VALUE_SCHEMA,
  vin: VIN_VALUE_SCHEMA,
};

export const DL_STATE_SCHEMA: Yup.StringSchema = Yup.string().test(
  'dl-state',
  'Invalid state.',
  function (value: string | undefined) {
    // Reuse drivers_license validator with a valid number stand-in so only
    // the state branch errors propagate. dl_number has its own schema.
    const err = validateAttributeValue('drivers_license', {
      state: value ?? '',
      number: 'AAAA',
    });
    return err ? this.createError({ message: err }) : true;
  }
);

export const DL_NUMBER_SCHEMA: Yup.StringSchema = Yup.string().test(
  'dl-number',
  'Invalid driver license.',
  function (value: string | undefined) {
    const err = validateAttributeValue('drivers_license', {
      state: 'CA',
      number: value ?? '',
    });
    return err ? this.createError({ message: err }) : true;
  }
);

// ---------------------------------------------------------------------------
// Name & DOB (3 fields: first_name, last_name, dob in MM/DD/YYYY)
// ---------------------------------------------------------------------------

export const NAME_DOB_FIRST_NAME_SCHEMA: Yup.StringSchema = Yup.string()
  .trim()
  .required('First name is required.');

export const NAME_DOB_LAST_NAME_SCHEMA: Yup.StringSchema = Yup.string()
  .trim()
  .required('Last name is required.');

export const NAME_DOB_DOB_SCHEMA: Yup.StringSchema = Yup.string()
  .required('Date of birth is required.')
  .matches(/^\d{2}\/\d{2}\/\d{4}$/, 'Enter a valid date (MM/DD/YYYY).')
  .test('valid-calendar-date', 'Enter a valid date (MM/DD/YYYY).', (value) => {
    if (!value) return true;
    const [mm, dd, yyyy] = value.split('/');
    const year = Number(yyyy);
    if (year < 1900) return false;
    const parsed = new Date(`${yyyy}-${mm}-${dd}`);
    return (
      !isNaN(parsed.getTime()) &&
      parsed.getUTCMonth() + 1 === Number(mm) &&
      parsed.getUTCDate() === Number(dd)
    );
  })
  .test('not-in-future', 'Date of birth cannot be in the future.', (value) => {
    if (!value) return true;
    const [mm, dd, yyyy] = value.split('/');
    const today = new Date();
    const todayLocal = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    return `${yyyy}-${mm}-${dd}` <= todayLocal;
  });

/**
 * Top-level form validation schema for AddAttributeModal.
 *
 * Conditional fields:
 *   attribute_value — required + format-validated for single-value types
 *                     (email/email_domain/phone_number/vin); ignored for
 *                     drivers_license (uses dl_state + dl_number instead)
 *                     and name_dob (excluded from MVP).
 *   dl_state, dl_number — required + format-validated when
 *                          attribute_type === 'drivers_license'.
 *
 * Always validated:
 *   attribute_type, reason — required
 *   notes — required, max 500 chars (matches input maxLength)
 *
 * Optional:
 *   policy_number
 */
export const addAttributeFormSchema = Yup.object().shape({
  attribute_type: Yup.string().required('Attribute type is required.'),
  reason: Yup.string().required('Block reason is required.'),
  notes: Yup.string()
    .trim()
    .required('Notes are required.')
    .max(500, 'Notes cannot exceed 500 characters.'),
  policy_number: Yup.string(),
  attribute_value: Yup.string().when('attribute_type', {
    is: (val: string) =>
      !!val && val !== 'drivers_license' && val !== 'name_dob',
    then: (schema: Yup.StringSchema) =>
      schema.test(
        'per-type-value',
        'Invalid value.',
        function (value: string | undefined) {
          const type = this.parent.attribute_type as
            | SingleValueType
            | undefined;
          if (!type) return true;
          const perType = ATTRIBUTE_VALUE_SCHEMAS[type];
          if (!perType) return true;
          try {
            perType.validateSync(value ?? '');
            return true;
          } catch (e) {
            const message =
              e instanceof Yup.ValidationError ? e.message : 'Invalid value.';
            return this.createError({ message });
          }
        }
      ),
    otherwise: (schema: Yup.StringSchema) => schema,
  }),
  dl_state: Yup.string().when('attribute_type', {
    is: 'drivers_license',
    then: () => DL_STATE_SCHEMA,
    otherwise: (schema: Yup.StringSchema) => schema,
  }),
  dl_number: Yup.string().when('attribute_type', {
    is: 'drivers_license',
    then: () => DL_NUMBER_SCHEMA,
    otherwise: (schema: Yup.StringSchema) => schema,
  }),
  first_name: Yup.string().when('attribute_type', {
    is: 'name_dob',
    then: () => NAME_DOB_FIRST_NAME_SCHEMA,
    otherwise: (schema: Yup.StringSchema) => schema,
  }),
  last_name: Yup.string().when('attribute_type', {
    is: 'name_dob',
    then: () => NAME_DOB_LAST_NAME_SCHEMA,
    otherwise: (schema: Yup.StringSchema) => schema,
  }),
  dob: Yup.string().when('attribute_type', {
    is: 'name_dob',
    then: () => NAME_DOB_DOB_SCHEMA,
    otherwise: (schema: Yup.StringSchema) => schema,
  }),
});
